/**
 * Cartões compartilháveis, desenhados no próprio navegador.
 *
 * Resultado de partida, título conquistado e contratação viram uma imagem que o
 * jogador salva ou manda para o grupo. Tudo é feito com a API de canvas do
 * navegador: nada de biblioteca de imagem, nada de servidor. O mesmo cartão sai
 * igual no celular e no desktop, e sai offline.
 *
 * As cores não estão escritas aqui: são lidas de `src/styles/tokens.css` na
 * hora de desenhar. Assim o cartão acompanha o tema e qualquer ajuste de paleta
 * chega nele sem ninguém lembrar deste arquivo.
 */

import { rotuloDePosicao, t, type Chave } from "../i18n";

/** Tamanho do cartão, em pixels. Retrato, que é o formato que mais circula. */
export const LARGURA_DO_CARTAO = 1080;
export const ALTURA_DO_CARTAO = 1350;

const MARGEM = 80;

export interface IdentidadeDeClube {
  nome: string;
  sigla: string;
  cores: [string, string];
}

export type DadosDeCartao =
  | {
      tipo: "resultado";
      contexto: string;
      mandante: IdentidadeDeClube;
      visitante: IdentidadeDeClube;
      golsMandante: number;
      golsVisitante: number;
      resultado: "V" | "E" | "D";
      posse: number;
      chances: number;
      chancesDoRival: number;
      fatores: string[];
      classico: boolean;
      /** De que lado está o clube do jogador. Nulo quando a partida não diz. */
      meuLado: "mandante" | "visitante" | null;
    }
  | {
      tipo: "titulo";
      contexto: string;
      clube: IdentidadeDeClube;
      titulos: string[];
      liga: string;
      posicao: number;
      pontos: number;
      vitorias: number;
      saldo: number;
    }
  | {
      tipo: "contratacao";
      contexto: string;
      clube: IdentidadeDeClube;
      jogador: string;
      posicao: string;
      forca: number;
      idade: number;
      /** Como o negócio saiu: "por $ 2,4 mi" ou "sem custos". */
      negocio: string;
      destaques: string[];
    };

// ---------------------------------------------------------------------------
// Paleta
// ---------------------------------------------------------------------------

interface Paleta {
  fundo: string;
  fundoProfundo: string;
  superficie: string;
  borda: string;
  bordaForte: string;
  texto: string;
  textoFraco: string;
  textoApagado: string;
  primaria: string;
  destaque: string;
  perigo: string;
  aviso: string;
  sucesso: string;
  forcaBaixa: string;
  forcaMedia: string;
  forcaAlta: string;
  forcaEstrela: string;
  setores: Record<string, string>;
}

/** Valores do tema escuro, usados quando a variável não puder ser lida. */
const PADRAO: Record<string, string> = {
  "--bg": "#070a08",
  "--bg-deep": "#040605",
  "--surface": "#0d120f",
  "--border": "#1e2b23",
  "--border-strong": "#2c3f33",
  "--text": "#e8efe9",
  "--text-muted": "#7e8f84",
  "--text-dim": "#566159",
  "--primary": "#2fe06a",
  "--accent": "#f5c542",
  "--danger": "#e5484d",
  "--warning": "#f2a33a",
  "--success": "#2fe06a",
  "--strength-low": "#e5484d",
  "--strength-mid": "#f2a33a",
  "--strength-high": "#2fe06a",
  "--strength-star": "#f5c542",
  "--sector-gk": "#f2a33a",
  "--sector-def": "#4ea1ff",
  "--sector-mid": "#2fe06a",
  "--sector-att": "#e5484d",
};

function lerPaleta(): Paleta {
  const estilo =
    typeof window === "undefined" ? null : window.getComputedStyle(document.documentElement);
  const cor = (nome: string): string => {
    const lido = estilo?.getPropertyValue(nome).trim();
    return lido && lido.length > 0 ? lido : (PADRAO[nome] ?? "#ffffff");
  };

  return {
    fundo: cor("--bg"),
    fundoProfundo: cor("--bg-deep"),
    superficie: cor("--surface"),
    borda: cor("--border"),
    bordaForte: cor("--border-strong"),
    texto: cor("--text"),
    textoFraco: cor("--text-muted"),
    textoApagado: cor("--text-dim"),
    primaria: cor("--primary"),
    destaque: cor("--accent"),
    perigo: cor("--danger"),
    aviso: cor("--warning"),
    sucesso: cor("--success"),
    forcaBaixa: cor("--strength-low"),
    forcaMedia: cor("--strength-mid"),
    forcaAlta: cor("--strength-high"),
    forcaEstrela: cor("--strength-star"),
    setores: {
      GOL: cor("--sector-gk"),
      ZAG: cor("--sector-def"),
      LD: cor("--sector-def"),
      LE: cor("--sector-def"),
      VOL: cor("--sector-mid"),
      MC: cor("--sector-mid"),
      MEI: cor("--sector-mid"),
      PD: cor("--sector-att"),
      PE: cor("--sector-att"),
      ATA: cor("--sector-att"),
    },
  };
}

/** Mesma escala de força do resto do jogo: 85 dourado, 70 verde, 50 laranja. */
function corDaForca(valor: number, p: Paleta): string {
  if (valor >= 85) return p.forcaEstrela;
  if (valor >= 70) return p.forcaAlta;
  if (valor >= 50) return p.forcaMedia;
  return p.forcaBaixa;
}

/** Cor em rgba, para os brilhos de fundo. Cor que não for hex volta inteira. */
function comAlfa(cor: string, alfa: number): string {
  const limpa = cor.trim();
  const curto = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(limpa);
  const longo = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(limpa);
  if (curto) {
    const [, r = "0", g = "0", b = "0"] = curto;
    return `rgba(${parseInt(r + r, 16)}, ${parseInt(g + g, 16)}, ${parseInt(b + b, 16)}, ${alfa})`;
  }
  if (longo) {
    const [, r = "00", g = "00", b = "00"] = longo;
    return `rgba(${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}, ${alfa})`;
  }
  return limpa;
}

// ---------------------------------------------------------------------------
// Desenho básico
// ---------------------------------------------------------------------------

function fonte(peso: number, tamanho: number): string {
  return `${peso} ${tamanho}px "Inter", system-ui, "Segoe UI", Roboto, sans-serif`;
}

type Alinhamento = "esquerda" | "centro" | "direita";

/**
 * Texto com espaçamento entre letras.
 *
 * `letterSpacing` do canvas ainda não existe em todo navegador, e os rótulos em
 * caixa alta do jogo dependem do espaçamento para parecerem os mesmos da tela.
 */
function larguraEspacada(
  ctx: CanvasRenderingContext2D,
  texto: string,
  espaco: number,
): number {
  const letras = Array.from(texto);
  const soma = letras.reduce((total, letra) => total + ctx.measureText(letra).width, 0);
  return soma + espaco * Math.max(0, letras.length - 1);
}

function textoEspacado(
  ctx: CanvasRenderingContext2D,
  texto: string,
  x: number,
  y: number,
  espaco: number,
  alinhamento: Alinhamento = "esquerda",
): void {
  const letras = Array.from(texto);
  const larguras = letras.map((letra) => ctx.measureText(letra).width);
  const total = larguras.reduce((soma, largura) => soma + largura, 0) + espaco * (letras.length - 1);
  let cursor = alinhamento === "centro" ? x - total / 2 : alinhamento === "direita" ? x - total : x;
  const alinhamentoAnterior = ctx.textAlign;
  ctx.textAlign = "left";
  letras.forEach((letra, indice) => {
    ctx.fillText(letra, cursor, y);
    cursor += (larguras[indice] ?? 0) + espaco;
  });
  ctx.textAlign = alinhamentoAnterior;
}

/** Quebra o texto em linhas que cabem na largura pedida. */
function quebrar(ctx: CanvasRenderingContext2D, texto: string, largura: number): string[] {
  const palavras = texto.split(/\s+/).filter((palavra) => palavra.length > 0);
  const linhas: string[] = [];
  let atual = "";
  for (const palavra of palavras) {
    const tentativa = atual ? `${atual} ${palavra}` : palavra;
    if (ctx.measureText(tentativa).width <= largura || !atual) {
      atual = tentativa;
    } else {
      linhas.push(atual);
      atual = palavra;
    }
  }
  if (atual) linhas.push(atual);
  return linhas;
}

/**
 * Texto que encolhe até caber em uma linha.
 *
 * Nome de clube fictício pode ter trinta letras, e um cartão com o nome cortado
 * no meio não serve para mandar para ninguém.
 */
function textoQueCabe(
  ctx: CanvasRenderingContext2D,
  texto: string,
  x: number,
  y: number,
  largura: number,
  peso: number,
  tamanho: number,
  alinhamento: Alinhamento = "centro",
  minimo = 18,
): void {
  let corpo = tamanho;
  ctx.font = fonte(peso, corpo);
  while (ctx.measureText(texto).width > largura && corpo > minimo) {
    corpo -= 2;
    ctx.font = fonte(peso, corpo);
  }
  ctx.textAlign =
    alinhamento === "centro" ? "center" : alinhamento === "direita" ? "right" : "left";
  ctx.fillText(texto, x, y);
  ctx.textAlign = "left";
}

/**
 * Quebra o texto em no máximo tantas linhas, diminuindo a letra até caber.
 *
 * Nome de jogador não pode sair cortado de um cartão que vai para o grupo do
 * time: antes de perder uma palavra, o cartão perde tamanho de fonte.
 */
function linhasQueCabem(
  ctx: CanvasRenderingContext2D,
  texto: string,
  largura: number,
  peso: number,
  tamanho: number,
  maxLinhas: number,
  minimo: number,
): { linhas: string[]; tamanho: number } {
  let corpo = tamanho;
  for (;;) {
    ctx.font = fonte(peso, corpo);
    const linhas = quebrar(ctx, texto, largura);
    if (linhas.length <= maxLinhas) return { linhas, tamanho: corpo };
    if (corpo <= minimo) {
      const cortadas = linhas.slice(0, maxLinhas);
      const ultima = cortadas[maxLinhas - 1];
      if (ultima) cortadas[maxLinhas - 1] = `${ultima}...`;
      return { linhas: cortadas, tamanho: corpo };
    }
    corpo -= 4;
  }
}

function caminhoArredondado(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  largura: number,
  altura: number,
  raio: number,
): void {
  const r = Math.min(raio, largura / 2, altura / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + largura, y, x + largura, y + altura, r);
  ctx.arcTo(x + largura, y + altura, x, y + altura, r);
  ctx.arcTo(x, y + altura, x, y, r);
  ctx.arcTo(x, y, x + largura, y, r);
  ctx.closePath();
}

/**
 * Escudo do clube, o mesmo desenho do componente `Crest` de `src/ui/atoms.tsx`.
 *
 * O caminho é copiado do SVG para o cartão ter a mesma leitura visual da tela.
 * A sigla é desenhada fora da escala do escudo, senão a letra sairia esticada,
 * porque o escudo não é quadrado (40 por 44) e o cartão o usa em caixa quadrada.
 */
function desenharEscudo(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  tamanho: number,
  cores: [string, string],
  sigla: string,
): void {
  const [principal, secundaria] = cores;
  const corpo = new Path2D("M20 1 38 6v20c0 8-8 14-18 17C10 40 2 34 2 26V6Z");
  const metade = new Path2D("M20 1 38 6v20c0 8-8 14-18 17V1Z");

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(tamanho / 40, tamanho / 44);
  ctx.fillStyle = principal;
  ctx.fill(corpo);
  ctx.globalAlpha = 0.55;
  ctx.fillStyle = secundaria;
  ctx.fill(metade);
  ctx.globalAlpha = 1;
  ctx.strokeStyle = "rgba(255,255,255,0.28)";
  ctx.lineWidth = 1.5;
  ctx.stroke(corpo);
  ctx.restore();

  ctx.save();
  ctx.font = fonte(800, tamanho * 0.3);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.lineWidth = tamanho * 0.08;
  ctx.strokeStyle = "rgba(0,0,0,0.45)";
  ctx.strokeText(sigla, x + tamanho / 2, y + tamanho * 0.48);
  ctx.fillStyle = "#ffffff";
  ctx.fillText(sigla, x + tamanho / 2, y + tamanho * 0.48);
  ctx.restore();
}

/** Selo de posição, com a cor do setor, como o `PositionChip` da tela. */
function selo(
  ctx: CanvasRenderingContext2D,
  texto: string,
  x: number,
  y: number,
  cor: string,
  tamanho = 30,
): number {
  ctx.font = fonte(700, tamanho);
  const largura = ctx.measureText(texto).width + tamanho * 1.6;
  const altura = tamanho * 2;
  caminhoArredondado(ctx, x, y, largura, altura, 10);
  ctx.strokeStyle = cor;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = comAlfa(cor, 0.12);
  ctx.fill();
  ctx.fillStyle = cor;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(texto, x + largura / 2, y + altura / 2 + 1);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  return largura;
}

// ---------------------------------------------------------------------------
// Moldura
// ---------------------------------------------------------------------------

/** O cartão é do clube do jogador, então o fundo puxa as cores dele. */
function coresDoCartao(dados: DadosDeCartao): [string, string] {
  if (dados.tipo === "resultado") {
    return dados.meuLado === "visitante" ? dados.visitante.cores : dados.mandante.cores;
  }
  return dados.clube.cores;
}

function rodapeDoCartao(dados: DadosDeCartao): string {
  if (dados.tipo === "resultado") {
    if (dados.meuLado === "mandante") return dados.mandante.nome;
    if (dados.meuLado === "visitante") return dados.visitante.nome;
    return `${dados.mandante.nome} contra ${dados.visitante.nome}`;
  }
  return dados.clube.nome;
}

function moldura(ctx: CanvasRenderingContext2D, p: Paleta, dados: DadosDeCartao): void {
  const [primeira, segunda] = coresDoCartao(dados);

  ctx.fillStyle = p.fundoProfundo;
  ctx.fillRect(0, 0, LARGURA_DO_CARTAO, ALTURA_DO_CARTAO);

  // Brilho com a cor do clube, o mesmo efeito do fundo do hub.
  const brilho = ctx.createRadialGradient(
    LARGURA_DO_CARTAO / 2,
    340,
    30,
    LARGURA_DO_CARTAO / 2,
    340,
    820,
  );
  brilho.addColorStop(0, comAlfa(primeira, 0.26));
  brilho.addColorStop(1, comAlfa(primeira, 0));
  ctx.fillStyle = brilho;
  ctx.fillRect(0, 0, LARGURA_DO_CARTAO, ALTURA_DO_CARTAO);

  // Faixa das duas cores do clube no topo.
  ctx.fillStyle = primeira;
  ctx.fillRect(0, 0, LARGURA_DO_CARTAO * 0.62, 12);
  ctx.fillStyle = segunda;
  ctx.fillRect(LARGURA_DO_CARTAO * 0.62, 0, LARGURA_DO_CARTAO * 0.38, 12);

  // Rótulo do topo e a linha que separa do corpo.
  ctx.fillStyle = p.primaria;
  ctx.font = fonte(700, 24);
  ctx.textBaseline = "alphabetic";
  textoEspacado(ctx, dados.contexto.toUpperCase(), MARGEM, 110, 5);

  ctx.strokeStyle = p.borda;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(MARGEM, 142);
  ctx.lineTo(LARGURA_DO_CARTAO - MARGEM, 142);
  ctx.stroke();

  // Rodapé: de quem é o cartão à esquerda, a marca à direita.
  ctx.beginPath();
  ctx.moveTo(MARGEM, ALTURA_DO_CARTAO - 132);
  ctx.lineTo(LARGURA_DO_CARTAO - MARGEM, ALTURA_DO_CARTAO - 132);
  ctx.stroke();

  // A marca é medida antes para o nome do clube nunca escrever por cima dela.
  ctx.font = fonte(800, 22);
  const larguraDaMarca = larguraEspacada(ctx, "LEGACY MANAGER", 6);

  ctx.fillStyle = p.textoFraco;
  textoQueCabe(
    ctx,
    rodapeDoCartao(dados),
    MARGEM,
    ALTURA_DO_CARTAO - 76,
    LARGURA_DO_CARTAO - MARGEM * 2 - larguraDaMarca - 40,
    600,
    26,
    "esquerda",
    14,
  );

  ctx.fillStyle = p.primaria;
  ctx.font = fonte(800, 22);
  textoEspacado(
    ctx,
    "LEGACY MANAGER",
    LARGURA_DO_CARTAO - MARGEM,
    ALTURA_DO_CARTAO - 76,
    6,
    "direita",
  );
}

// ---------------------------------------------------------------------------
// Corpo de cada cartão
// ---------------------------------------------------------------------------

/*
 * Chave do dicionário para cada letra de resultado, e não o texto pronto: o
 * cartão sai no idioma em que o jogo está, porque é nesse idioma que quem o
 * recebe vai lê-lo.
 */
const ROTULO_DO_RESULTADO: Record<string, Chave> = {
  V: "partida.vitoria",
  E: "partida.empate",
  D: "partida.derrota",
};

function corpoResultado(
  ctx: CanvasRenderingContext2D,
  p: Paleta,
  dados: Extract<DadosDeCartao, { tipo: "resultado" }>,
): void {
  const meio = LARGURA_DO_CARTAO / 2;
  const tamanhoEscudo = 210;
  const centroMandante = MARGEM + 150;
  const centroVisitante = LARGURA_DO_CARTAO - MARGEM - 150;

  if (dados.classico) {
    ctx.font = fonte(800, 22);
    ctx.fillStyle = p.perigo;
    textoEspacado(ctx, "CLÁSSICO", LARGURA_DO_CARTAO - MARGEM, 110, 5, "direita");
  }

  desenharEscudo(
    ctx,
    centroMandante - tamanhoEscudo / 2,
    230,
    tamanhoEscudo,
    dados.mandante.cores,
    dados.mandante.sigla,
  );
  desenharEscudo(
    ctx,
    centroVisitante - tamanhoEscudo / 2,
    230,
    tamanhoEscudo,
    dados.visitante.cores,
    dados.visitante.sigla,
  );

  ctx.fillStyle = p.texto;
  ctx.font = fonte(800, 132);
  ctx.textAlign = "center";
  ctx.fillText(`${dados.golsMandante}`, meio - 78, 385);
  ctx.fillText(`${dados.golsVisitante}`, meio + 78, 385);
  ctx.fillStyle = p.textoApagado;
  ctx.font = fonte(400, 80);
  ctx.fillText("x", meio, 380);
  ctx.textAlign = "left";

  ctx.fillStyle = p.texto;
  textoQueCabe(ctx, dados.mandante.nome, centroMandante, 510, 300, 700, 34);
  textoQueCabe(ctx, dados.visitante.nome, centroVisitante, 510, 300, 700, 34);

  // Traço na cor da marca embaixo do clube de quem está compartilhando.
  if (dados.meuLado) {
    const centro = dados.meuLado === "mandante" ? centroMandante : centroVisitante;
    ctx.fillStyle = p.primaria;
    caminhoArredondado(ctx, centro - 40, 530, 80, 5, 3);
    ctx.fill();
  }

  const cor =
    dados.resultado === "V" ? p.sucesso : dados.resultado === "E" ? p.aviso : p.perigo;
  ctx.fillStyle = cor;
  ctx.font = fonte(800, 66);
  ctx.textAlign = "center";
  const rotulo = ROTULO_DO_RESULTADO[dados.resultado];
  ctx.fillText(rotulo ? t(rotulo) : "", meio, 630);

  ctx.fillStyle = p.textoFraco;
  ctx.font = fonte(500, 30);
  ctx.fillText(
    `${t("partida.posse", { valor: dados.posse })}   ·   ${t("partida.chances", {
      minhas: dados.chances,
      dele: dados.chancesDoRival,
    })}`,
    meio,
    692,
  );
  ctx.textAlign = "left";

  if (dados.fatores.length === 0) return;

  ctx.fillStyle = p.primaria;
  ctx.font = fonte(700, 22);
  textoEspacado(ctx, "POR QUE ACONTECEU", MARGEM, 790, 5);

  let y = 852;
  ctx.font = fonte(500, 30);
  for (const fator of dados.fatores.slice(0, 3)) {
    const linhas = quebrar(ctx, fator, LARGURA_DO_CARTAO - MARGEM * 2 - 34);
    ctx.fillStyle = p.primaria;
    ctx.beginPath();
    ctx.arc(MARGEM + 7, y - 11, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = p.texto;
    for (const linha of linhas) {
      ctx.fillText(linha, MARGEM + 34, y);
      y += 42;
    }
    y += 14;
  }
}

function corpoTitulo(
  ctx: CanvasRenderingContext2D,
  p: Paleta,
  dados: Extract<DadosDeCartao, { tipo: "titulo" }>,
): void {
  const meio = LARGURA_DO_CARTAO / 2;
  const tamanhoEscudo = 250;

  desenharEscudo(
    ctx,
    meio - tamanhoEscudo / 2,
    214,
    tamanhoEscudo,
    dados.clube.cores,
    dados.clube.sigla,
  );

  ctx.fillStyle = p.destaque;
  ctx.font = fonte(700, 26);
  textoEspacado(
    ctx,
    dados.titulos.length > 1 ? "TÍTULOS CONQUISTADOS" : "TÍTULO CONQUISTADO",
    meio,
    546,
    6,
    "centro",
  );

  let y = 636;
  for (const titulo of dados.titulos.slice(0, 3)) {
    ctx.fillStyle = p.destaque;
    textoQueCabe(ctx, titulo, meio, y, LARGURA_DO_CARTAO - MARGEM * 2, 800, 68, "centro", 34);
    y += 88;
  }

  ctx.fillStyle = p.texto;
  textoQueCabe(ctx, dados.clube.nome, meio, y + 26, LARGURA_DO_CARTAO - MARGEM * 2, 700, 44);

  ctx.fillStyle = p.textoFraco;
  ctx.font = fonte(500, 28);
  ctx.textAlign = "center";
  ctx.fillText(
    `${t("fimDeTemporada.posicaoValor", { posicao: dados.posicao })} ${t(
      "fimDeTemporada.naLiga",
      { liga: dados.liga },
    )}`,
    meio,
    y + 82,
  );
  ctx.textAlign = "left";

  // Faixa de números da campanha, com a mesma leitura do painel de desempenho.
  const largura = LARGURA_DO_CARTAO - MARGEM * 2;
  const alturaFaixa = 150;
  const topo = ALTURA_DO_CARTAO - 132 - 60 - alturaFaixa;
  caminhoArredondado(ctx, MARGEM, topo, largura, alturaFaixa, 14);
  ctx.fillStyle = p.superficie;
  ctx.fill();
  ctx.strokeStyle = p.borda;
  ctx.lineWidth = 2;
  ctx.stroke();

  const numeros: Array<[string, string, string]> = [
    [t("fimDeTemporada.pontos"), String(dados.pontos), p.primaria],
    [t("fimDeTemporada.vitorias"), String(dados.vitorias), p.texto],
    [
      t("fimDeTemporada.saldo"),
      dados.saldo > 0 ? `+${dados.saldo}` : String(dados.saldo),
      p.texto,
    ],
  ];
  numeros.forEach(([rotulo, valor, cor], indice) => {
    const x = MARGEM + (largura / 3) * (indice + 0.5);
    ctx.fillStyle = cor;
    ctx.font = fonte(800, 54);
    ctx.textAlign = "center";
    ctx.fillText(valor, x, topo + 78);
    ctx.fillStyle = p.textoApagado;
    ctx.font = fonte(700, 20);
    textoEspacado(ctx, rotulo.toUpperCase(), x, topo + 118, 5, "centro");
    ctx.textAlign = "left";
  });
}

function corpoContratacao(
  ctx: CanvasRenderingContext2D,
  p: Paleta,
  dados: Extract<DadosDeCartao, { tipo: "contratacao" }>,
): void {
  const tamanhoEscudo = 190;

  desenharEscudo(ctx, MARGEM, 214, tamanhoEscudo, dados.clube.cores, dados.clube.sigla);

  ctx.fillStyle = p.primaria;
  ctx.font = fonte(700, 24);
  textoEspacado(
    ctx,
    t("noticias.reforcoConfirmado").toUpperCase(),
    MARGEM + tamanhoEscudo + 34,
    286,
    6,
  );

  ctx.fillStyle = p.texto;
  textoQueCabe(
    ctx,
    dados.clube.nome,
    MARGEM + tamanhoEscudo + 34,
    348,
    LARGURA_DO_CARTAO - MARGEM * 2 - tamanhoEscudo - 34,
    700,
    42,
    "esquerda",
  );

  const nome = linhasQueCabem(
    ctx,
    dados.jogador,
    LARGURA_DO_CARTAO - MARGEM * 2,
    800,
    84,
    2,
    44,
  );
  ctx.fillStyle = p.texto;
  ctx.font = fonte(800, nome.tamanho);
  let y = 560;
  for (const linha of nome.linhas) {
    ctx.fillText(linha, MARGEM, y);
    y += nome.tamanho * 1.15;
  }

  const linhaDeSelos = y + 16;
  let x = MARGEM;
  // A cor do selo sai da sigla do motor, o texto sai do idioma ativo.
  x +=
    selo(
      ctx,
      rotuloDePosicao(dados.posicao),
      x,
      linhaDeSelos,
      p.setores[dados.posicao] ?? p.textoFraco,
    ) + 16;
  x +=
    selo(
      ctx,
      `${t("fimDeTemporada.colunas.forca")} ${dados.forca}`,
      x,
      linhaDeSelos,
      corDaForca(dados.forca, p),
    ) + 16;
  selo(ctx, t("tatica.idadeDoCandidato", { idade: dados.idade }), x, linhaDeSelos, p.textoFraco);

  /*
   * O negócio e os destaques moram numa faixa presa ao rodapé.
   *
   * Nome de uma linha e nome de duas linhas deixariam o cartão com buracos de
   * tamanhos diferentes embaixo. Preso ao rodapé, o cartão fecha igual sempre.
   */
  const largura = LARGURA_DO_CARTAO - MARGEM * 2;
  const alturaFaixa = 150;
  const topo = ALTURA_DO_CARTAO - 132 - 60 - alturaFaixa;
  caminhoArredondado(ctx, MARGEM, topo, largura, alturaFaixa, 14);
  ctx.fillStyle = p.superficie;
  ctx.fill();
  ctx.strokeStyle = p.borda;
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.fillStyle = p.textoApagado;
  ctx.font = fonte(700, 20);
  textoEspacado(ctx, "NEGÓCIO", MARGEM + 34, topo + 48, 5);
  ctx.fillStyle = p.destaque;
  textoQueCabe(
    ctx,
    dados.negocio,
    MARGEM + 34,
    topo + 106,
    largura / 2 - 50,
    700,
    38,
    "esquerda",
    22,
  );

  if (dados.destaques.length > 0) {
    ctx.fillStyle = p.textoApagado;
    ctx.font = fonte(700, 20);
    textoEspacado(ctx, "DESTAQUES", LARGURA_DO_CARTAO - MARGEM - 34, topo + 48, 5, "direita");
    ctx.fillStyle = p.texto;
    textoQueCabe(
      ctx,
      dados.destaques.join("   ·   "),
      LARGURA_DO_CARTAO - MARGEM - 34,
      topo + 106,
      largura / 2 - 50,
      500,
      32,
      "direita",
      20,
    );
  }
}

// ---------------------------------------------------------------------------
// Entradas públicas
// ---------------------------------------------------------------------------

/** Desenha o cartão inteiro no contexto, do fundo ao rodapé. */
export function desenharCartao(ctx: CanvasRenderingContext2D, dados: DadosDeCartao): void {
  const p = lerPaleta();
  ctx.save();
  ctx.clearRect(0, 0, LARGURA_DO_CARTAO, ALTURA_DO_CARTAO);
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  moldura(ctx, p, dados);
  if (dados.tipo === "resultado") corpoResultado(ctx, p, dados);
  else if (dados.tipo === "titulo") corpoTitulo(ctx, p, dados);
  else corpoContratacao(ctx, p, dados);
  ctx.restore();
}

/** Nome do arquivo, sem acento e sem espaço, para salvar e compartilhar. */
export function nomeDoArquivo(dados: DadosDeCartao): string {
  const bruto =
    dados.tipo === "resultado"
      ? `${dados.mandante.sigla}-${dados.golsMandante}-${dados.golsVisitante}-${dados.visitante.sigla}`
      : dados.tipo === "titulo"
        ? `${dados.clube.sigla}-${dados.titulos[0] ?? "titulo"}`
        : `${dados.clube.sigla}-${dados.jogador}`;
  const limpo = bruto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
  return `legacy-manager-${dados.tipo}-${limpo}.png`;
}

/** Frase que acompanha a imagem quando o sistema pede um texto. */
export function textoDoCartao(dados: DadosDeCartao): string {
  if (dados.tipo === "resultado") {
    return `${dados.mandante.nome} ${dados.golsMandante} x ${dados.golsVisitante} ${dados.visitante.nome}, pelo ${dados.contexto}.`;
  }
  if (dados.tipo === "titulo") {
    return `${dados.clube.nome} campeão: ${dados.titulos.join(", ")}.`;
  }
  return `${dados.jogador} é do ${dados.clube.nome}.`;
}

/** A imagem do canvas como arquivo PNG. */
export function imagemDoCanvas(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolver, rejeitar) => {
    canvas.toBlob((blob) => {
      if (blob) resolver(blob);
      else rejeitar(new Error("O navegador não conseguiu gerar a imagem do cartão."));
    }, "image/png");
  });
}

/** O navegador tem a folha de compartilhamento do sistema. */
export function temCompartilhamento(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

export type SaidaDoCartao = "compartilhado" | "baixado" | "cancelado";

/**
 * Manda o cartão para onde o sistema puder.
 *
 * No celular, `navigator.share` abre a folha do sistema e o jogador escolhe o
 * aplicativo. No desktop, onde quase nunca existe, o cartão vira download. A
 * queda também acontece quando o navegador tem a folha mas recusa arquivos.
 */
export async function compartilharOuBaixar(
  blob: Blob,
  nome: string,
  titulo: string,
  texto: string,
): Promise<SaidaDoCartao> {
  if (temCompartilhamento()) {
    const arquivo = new File([blob], nome, { type: "image/png" });
    const carga: ShareData = { files: [arquivo], title: titulo, text: texto };
    const aceita =
      typeof navigator.canShare === "function" ? navigator.canShare(carga) : true;
    if (aceita) {
      try {
        await navigator.share(carga);
        return "compartilhado";
      } catch (erro) {
        if (erro instanceof Error && erro.name === "AbortError") return "cancelado";
        // Qualquer outra recusa vira download, para o jogador não ficar sem nada.
      }
    }
  }
  baixarImagem(blob, nome);
  return "baixado";
}

/** Salva a imagem como arquivo, que é a saída de quem não tem folha de compartilhamento. */
export function baixarImagem(blob: Blob, nome: string): void {
  const endereco = URL.createObjectURL(blob);
  const ancora = document.createElement("a");
  ancora.href = endereco;
  ancora.download = nome;
  document.body.appendChild(ancora);
  ancora.click();
  ancora.remove();
  window.setTimeout(() => URL.revokeObjectURL(endereco), 10_000);
}
